import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface StaticQRCode {
  team: string;
  qrCodeDataUrl: string;
  joinUrl: string;
}

const TEAM_COLORS: Record<string, { bg: string; text: string }> = {
  BLUE: { bg: 'bg-blue-600', text: 'text-blue-100' },
  RED: { bg: 'bg-red-600', text: 'text-red-100' },
  GREEN: { bg: 'bg-green-600', text: 'text-green-100' },
  YELLOW: { bg: 'bg-yellow-600', text: 'text-yellow-100' },
};

const TEAM_NAMES: Record<string, string> = {
  BLUE: 'Blu',
  RED: 'Rossa',
  GREEN: 'Verde',
  YELLOW: 'Gialla',
};

function QRCodesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const [qrCodes, setQrCodes] = useState<StaticQRCode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQRCodes();
  }, []);

  const fetchQRCodes = async () => {
    try {
      const response = await fetch('/api/qr/static');
      if (response.ok) {
        const data = await response.json();
        setQrCodes(data.qrCodes);
      }
    } catch (error) {
      console.error('Failed to fetch QR codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = (team: string, dataUrl: string) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `qr-code-team-${team.toLowerCase()}.png`;
    link.click();
  };

  const downloadAllQRCodes = () => {
    qrCodes.forEach((qr) => {
      downloadQRCode(qr.team, qr.qrCodeDataUrl);
    });
  };

  const printQRCodes = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">{t('admin:qrCodes.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="text-purple-400 hover:text-purple-300 mb-4"
          >
            ← {t('admin:navigation.backToDashboard')}
          </button>

          <h1 className="text-4xl font-bold text-white mb-2">
            🎫 {t('admin:qrCodes.title')}
          </h1>
          <p className="text-gray-400 text-lg">
            {t('admin:qrCodes.pageSubtitle')}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={downloadAllQRCodes}
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition"
          >
            📥 {t('admin:qrCodes.downloadAll')}
          </button>

          <button
            onClick={printQRCodes}
            className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition"
          >
            🖨️ {t('admin:qrCodes.print')}
          </button>
        </div>

        {/* QR Codes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 print:grid-cols-2">
          {qrCodes.map((qr) => {
            const colors = TEAM_COLORS[qr.team];

            return (
              <div
                key={qr.team}
                className={`${colors.bg} rounded-2xl p-8 shadow-2xl print:break-inside-avoid`}
              >
                <div className="text-center mb-6">
                  <h2 className={`text-4xl font-bold ${colors.text} uppercase mb-2`}>
                    {t('admin:qrCodes.team')} {TEAM_NAMES[qr.team] || qr.team}
                  </h2>
                  <p className={`text-lg ${colors.text} opacity-90`}>
                    {t('admin:qrCodes.scanToJoin')}
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl mb-6">
                  <img
                    src={qr.qrCodeDataUrl}
                    alt={`QR Code Squadra ${TEAM_NAMES[qr.team] || qr.team}`}
                    className="w-full h-auto"
                  />
                </div>

                <div className="text-center">
                  <button
                    onClick={() => downloadQRCode(qr.team, qr.qrCodeDataUrl)}
                    className="bg-white/20 hover:bg-white/30 text-white font-semibold py-2 px-6 rounded-lg transition print:hidden"
                  >
                    📥 {t('admin:qrCodes.downloadSingle')}
                  </button>
                </div>

                <div className="mt-4 text-center">
                  <p className={`text-sm ${colors.text} opacity-75 break-all`}>
                    {qr.joinUrl}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Instructions */}
        <div className="mt-12 bg-gray-800 rounded-xl p-6 border border-gray-700 print:hidden">
          <h3 className="text-xl font-bold text-white mb-4">📋 {t('admin:qrCodes.instructions.title')}</h3>
          <ul className="space-y-2 text-gray-300">
            <li>✅ {t('admin:qrCodes.instructions.validForever')}</li>
            <li>✅ {t('admin:qrCodes.instructions.autoAssign')}</li>
            <li>✅ {t('admin:qrCodes.instructions.reusable')}</li>
            <li>⚠️ {t('admin:qrCodes.instructions.requireActive')}</li>
            <li>💡 {t('admin:qrCodes.instructions.tip')}</li>
          </ul>
        </div>
      </div>

      <style>{`
        @media print {
          body { margin: 0; }
          @page { size: A4; margin: 1cm; }
        }
      `}</style>
    </div>
  );
}

export default QRCodesPage;
